import { prefersReducedMotion } from "@scripts/motion";
import type { ComponentInit } from "@scripts/registry";
import { FA_TAB_PAINT, FA_TAB_SELECT, SELECTED_TAB } from "@scripts/tabs";
import { createTracker } from "@scripts/tracker";

const TABS = "[data-fa-sponsor-block-tabs]";
const TAB = "[data-fa-tab]";
const PANEL = "[data-fa-sponsor-block-panel]";
const TRACKER = "[data-fa-sponsor-block-tracker]";
const TRACKING = "fa-sponsor-block__tabs--tracking";
const PAUSED_BY_READER = '[data-fa-video-pause][aria-pressed="true"]';

export const initSponsorBlock: ComponentInit = (root) => {
	const list = root.querySelector<HTMLElement>(TABS);

	if (!list) return;

	const listeners = new AbortController();
	const options = { signal: listeners.signal };

	const playback = (panel: HTMLElement, chosen: boolean) => {
		for (const video of panel.querySelectorAll("video")) {
			if (!chosen) {
				video.pause();
				continue;
			}

			if (prefersReducedMotion() || panel.querySelector(PAUSED_BY_READER)) continue;

			video.currentTime = 0;
			void video.play().catch(() => undefined);
		}
	};

	const centre = (tab: HTMLElement) => {
		list.scrollTo({
			left: tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2,
			behavior: prefersReducedMotion() ? "auto" : "smooth",
		});
	};

	root.addEventListener(
		FA_TAB_PAINT,
		(event) => {
			const { panel, selected } = event.detail;

			if (panel) playback(panel, selected);
		},
		options,
	);

	root.addEventListener(FA_TAB_SELECT, (event) => centre(event.detail.tab), options);

	/* `runRegistry` starts every entry under `Promise.all`, so the tab widget's own first paint
	   may already have fired by the time this listener attaches. */
	for (const panel of root.querySelectorAll<HTMLElement>(PANEL))
		playback(panel, !panel.hasAttribute("hidden"));

	/* Capture, because `play` does not bubble. A listener rather than an ordering assumption:
	   `runRegistry` starts every entry under `Promise.all`, so whether the Video chunk initialises
	   before or after this one is a race. */
	root.addEventListener(
		"play",
		(event) => {
			const video = event.target;

			if (!(video instanceof HTMLVideoElement)) return;
			if (video.closest(PANEL)?.hasAttribute("hidden")) video.pause();
		},
		{ ...options, capture: true },
	);

	const indicator = root.querySelector<HTMLElement>(TRACKER);
	const tracker = indicator
		? createTracker({
				root: list,
				tracker: indicator,
				item: TAB,
				restTarget: () => list.querySelector<HTMLElement>(SELECTED_TAB),
				trackingClass: TRACKING,
				followsHeight: false,
			})
		: undefined;

	return () => {
		listeners.abort();
		tracker?.();
	};
};
